import { ReactNode, useMemo } from 'react';

import { RiskContext } from '../../../../../providers/portfolio-performance/risk';
import {
  METRICS,
  MetricData,
  MetricKey,
  RiskContextValue,
} from '../../../../../providers/portfolio-performance/risk/risk.types';
import { IPortfolioPerformanceAccounts } from '../../../../../providers/portfolio-performance/portfolio-performance.types';

interface BacktestRiskProviderProps {
  metricsData: Record<MetricKey, MetricData>;
  token: string;
  strategyName: string;
  children: ReactNode;
}

const NOOP = () => {};
const NOOP_UNSUB = () => () => {};

const ALL_FALSE = METRICS.reduce(
  (acc, m) => ({ ...acc, [m]: false }),
  {} as Record<MetricKey, boolean>,
);

// Satisfies the live RiskContext from a completed backtest scenario so the
// shared RiskPanelCard/RiskPanelGraph render verbatim over the greeks already
// present in pnl_reports. Everything the live provider does over the wire is
// inert here: the full series is supplied up front, so there's no loading,
// error, live tick, historical paging, or refetch. The filter-bar setters are
// no-ops too — BacktestRiskPanel deliberately omits RiskPanelFilters (token /
// range / BASE-QUOTE selectors are meaningless for a fixed, single-stratlet
// full-run series), so nothing ever calls them.
export const BacktestRiskProvider = ({
  metricsData,
  token,
  strategyName,
  children,
}: BacktestRiskProviderProps) => {
  const account = useMemo<IPortfolioPerformanceAccounts>(
    () => ({ last_tick: '', name: strategyName, symbols: [token], exchange: '', venue: '' }),
    [strategyName, token],
  );

  const value = useMemo<RiskContextValue>(
    () => ({
      account,
      selectedToken: token,
      setSelectedToken: NOOP,
      // RiskPanelGraph reads selectedRange only as a chart viewport-reset key;
      // a constant keeps the static series from re-fitting on its own.
      selectedRange: '1D',
      setSelectedRange: NOOP,
      selectedMetrics: [...METRICS],
      setSelectedMetrics: NOOP,
      // QUOTE => 2dp in RiskPanelGraph, matching the live risk panel default.
      riskTokenType: 'QUOTE',
      setRiskTokenType: NOOP,
      metricsData,
      loadingMetrics: ALL_FALSE,
      errorMetrics: ALL_FALSE,
      refetchAll: NOOP,
      refetchMetric: NOOP,
      subscribeToPerformanceTick: NOOP_UNSUB,
      fetchHistoricalChunk: NOOP,
    }),
    [account, token, metricsData],
  );

  return <RiskContext.Provider value={value}>{children}</RiskContext.Provider>;
};
